import { inject, Injectable } from '@angular/core';
import { Mesh, ShaderMaterial } from 'three';
import { LocalStorageSetting, SessionService } from './session.service';

/**
 * Shared display settings service for all editor types.
 * Handles zoom, grid, dimensions, helpers, clipping, tooltips, and other
 * canvas-related display controls.
 *
 * Must be provided at the component level (not root) since it depends
 * on the engine instance from SessionService which is per-editor.
 */
@Injectable()
export class DisplaySettingsService {
  private session = inject(SessionService);

  // ── Zoom ──────────────────────────────────────────────────────────────

  zoomIn(): void {
    const engine = this.session.engine;
    if (engine?.controls) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const controls = engine.controls as any;
      if (controls.dollyIn) {
        controls.dollyIn(1.3);
        controls.update();
      } else if (engine.camera.zoom) {
        engine.camera.zoom *= 1.3;
        engine.camera.updateProjectionMatrix();
      }
    }
  }

  zoomOut(): void {
    const engine = this.session.engine;
    if (engine?.controls) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const controls = engine.controls as any;
      if (controls.dollyOut) {
        controls.dollyOut(1.3);
        controls.update();
      } else if (engine.camera.zoom) {
        engine.camera.zoom /= 1.3;
        engine.camera.updateProjectionMatrix();
      }
    }
  }

  // ── Grid ──────────────────────────────────────────────────────────────

  toggleGridVisible(value?: boolean): void {
    const engine = this.session.engine!;
    if (value !== undefined) {
      engine.gridVisible = value;
    } else {
      engine.gridVisible = !engine.gridVisible;
    }
    this.session.setSetting(
      LocalStorageSetting.LAYOUT_GRID,
      engine.gridVisible ? 'yes' : 'no'
    );
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  toggleHelpers(next?: boolean): boolean {
    const engine = this.session.engine!;
    const { drillBits, symmetryPlanes, scene } = engine;

    const shouldShow = typeof next === 'boolean' ? next : !engine.helpersVisible;

    if (!shouldShow) {
      [drillBits?.x1, drillBits?.y1, drillBits?.z1, drillBits?.x2, drillBits?.y2, drillBits?.z2].forEach((obj) =>
        obj?.removeFromParent()
      );
      [symmetryPlanes?.xy, symmetryPlanes?.xz, symmetryPlanes?.yz].forEach((plane) => plane?.removeFromParent());
      engine.hideArrow();
    } else {
      scene.add(drillBits.x1!, drillBits.y1!, drillBits.z1!, drillBits.x2!, drillBits.y2!, drillBits.z2!);
      scene.add(symmetryPlanes?.xy!, symmetryPlanes?.xz!, symmetryPlanes?.yz!);
      engine.showArrow();
    }

    engine.helpersVisible = shouldShow;

    this.session.setSetting(
      LocalStorageSetting.LAYOUT_HELPERS,
      this.session.engine?.helpersVisible === true ? 'yes' : 'no'
    );

    return engine.helpersVisible;
  }

  // ── Dimensions ────────────────────────────────────────────────────────

  toggleDimensionsVisible(): void {
    this.session.engine?.toggleDimensionsHelper();

    this.session.setSetting(
      LocalStorageSetting.LAYOUT_DIMENSIONS,
      this.session.engine?.dimensionsHelperVisible === true ? 'yes' : 'no'
    );
  }

  // ── Clipping ──────────────────────────────────────────────────────────

  clippingVisualisationChange(): void {
    setTimeout(() => {
      const engine = this.session.engine;
      if (engine) {
        engine.renderer.localClippingEnabled = engine.clippingEnabled;

        engine.scene.traverse((o) => {
          const isClippingHelper =
            o instanceof Mesh &&
            (o.name.endsWith('_SurfaceModel') ||
              o.name.endsWith('_BackSideModel') ||
              o.name.endsWith('_StencilFront') ||
              o.name.endsWith('_StencilBack') ||
              o.name === 'PlaneMeshX' ||
              o.name === 'PlaneMeshY' ||
              o.name === 'PlaneMeshZ');
          if (engine && isClippingHelper) {
            o.visible = engine.clippingEnabled;
          }
        });
      }
    });
  }

  clippingChange(value: number, type: 'x' | 'y' | 'z'): void {
    this.session.engine?.clipper.forEach((c) => c.setPosition(value, type));
  }

  clippingParametricChange(_value: number, _type: 'x' | 'y' | 'z'): void {
    // Reserved for future parametric clipping
  }

  // ── Load case / FEA ───────────────────────────────────────────────────

  toggleLoadCaseHighlight(value?: boolean): void {
    this.session.engine!.toggleLoadCaseHighlight(value);
  }

  toggleReactionForces(): void {
    this.session.engine!.toggleReactionForces();
  }

  animateTexture(_value?: boolean): void {
    setTimeout(() => {
      const feaMeshOrganic = this.session.engine?.infoGroup.children.find(
        (c) => c.userData['fea_type'] === 'organic'
      ) as Mesh;
      const feaMeshPrismatic = this.session.engine?.infoGroup.children.find(
        (c) => c.userData['fea_type'] === 'prismatic'
      ) as Mesh;

      if (feaMeshOrganic) {
        const material = feaMeshOrganic.material as ShaderMaterial;
        if (material && material.uniforms && material.uniforms['animate']) {
          material.uniforms['animate'].value = this.session.engine!.animateTexture === true ? 1 : 0;
        }
      }

      if (feaMeshPrismatic) {
        const material = feaMeshPrismatic.material as ShaderMaterial;
        if (material && material.uniforms && material.uniforms['animate']) {
          material.uniforms['animate'].value = this.session.engine!.animateTexture === true ? 1 : 0;
        }
      }
    });
  }

  currentDisplacementVisible(): string {
    if (this.session.project.flowVersion === 2) {
      const mesh = this.session.engine!.infoGroup.children.find(
        (c) => c.userData['fea_type'] === this.session.engine?.currentFEAOutput
      );
      for (const type of ['t', 'x', 'y', 'z', 's']) {
        if (mesh) {
          const meshVisible = mesh.visible;
          const meshCurrentDisplacementTypeMatch = mesh.userData['currentDisplacementType'] === type;
          if (meshVisible && meshCurrentDisplacementTypeMatch) return type;
        }
      }
    } else {
      for (const type of ['t', 'x', 'y', 'z', 's']) {
        const visible = this.session.engine!.infoGroup.getObjectByName(
          `lc_${this.session.engine!.currentLoadCase}_${type}`
        )!.visible;
        if (visible === true) return type;
      }
    }
    return 't';
  }

  // ── Tooltip helpers with dynamic keyboard shortcuts ───────────────────

  getShortcutKey(id: string): string {
    return this.session.keyboardShortcuts.getKey(id) || '';
  }

  getHomeViewTooltip(): string {
    const key = this.getShortcutKey('home-view');
    return key ? `Reset home view (${key})` : 'Reset home view';
  }

  getFitAllTooltip(): string {
    const key = this.getShortcutKey('fit-all');
    return key ? `Fit all in view (${key})` : 'Fit all in view';
  }

  getToggleGridTooltip(): string {
    const key = this.getShortcutKey('toggle-grid');
    return key ? `Toggle grid (${key})` : 'Toggle grid';
  }

  getToggleDimensionsTooltip(): string {
    const key = this.getShortcutKey('toggle-dimensions');
    return key ? `Toggle dimensions (${key})` : 'Toggle dimensions';
  }
}
